import { get, ref, update } from "firebase/database";
import { database } from "../firebase/firebaseConfig";
import { CoreError, CoreErrorCause } from "../../domain/models/CoreError";

/**
 * A character match held for delayed reveal. Timestamps are milliseconds.
 * `revealedAtMs` is set by the backend scheduler once the match has been
 * committed; the app clears the entry after presenting the story.
 */
export interface HeldCharacterMatch {
  partnershipId: string;
  characterId: string;
  cardId: string;
  revealAtMs: number;
  createdAtMs: number;
  revealedAtMs?: number;
}

const NODE = "characterHeldMatches";

/**
 * `users/{uid}/characterHeldMatches/{pid}/{cardId}` — the user-owned record
 * of held character matches. The backend mirrors every entry into its reveal
 * queue and commits it when due, so holds follow the user across devices and
 * browsers.
 */
export class CharacterHeldMatchService {
  private static instance: CharacterHeldMatchService;

  private constructor() {}

  public static getInstance(): CharacterHeldMatchService {
    if (!CharacterHeldMatchService.instance) {
      CharacterHeldMatchService.instance = new CharacterHeldMatchService();
    }
    return CharacterHeldMatchService.instance;
  }

  private toCoreError(error: unknown, context: string): CoreError {
    if (error instanceof CoreError) {
      return error;
    }
    return new CoreError({
      cause: CoreErrorCause.Unknown,
      context,
      underlyingException: error,
    });
  }

  private partnershipPath(userId: string, partnershipId: string): string {
    return `users/${userId}/${NODE}/${partnershipId}`;
  }

  public async loadForPartnership(
    userId: string,
    partnershipId: string,
  ): Promise<HeldCharacterMatch[]> {
    const path = this.partnershipPath(userId, partnershipId);
    const snapshot = await get(ref(database, path)).catch((error) => {
      throw this.toCoreError(error, `Failed to read /${path}`);
    });
    const data: unknown = snapshot.val();
    if (!data || typeof data !== "object") {
      return [];
    }
    const entries: HeldCharacterMatch[] = [];
    Object.entries(data as Record<string, unknown>).forEach(([cardId, raw]) => {
      const entry = this.toEntry(partnershipId, cardId, raw);
      if (entry) {
        entries.push(entry);
      }
    });
    return entries;
  }

  /**
   * Stores a hold unless one already exists for the card.
   *
   * @returns false on a dedupe hit
   */
  public async hold(
    userId: string,
    entry: HeldCharacterMatch,
  ): Promise<boolean> {
    const path = `${this.partnershipPath(userId, entry.partnershipId)}/${entry.cardId}`;
    const existing = await get(ref(database, path)).catch((error) => {
      throw this.toCoreError(error, `Failed to read /${path}`);
    });
    if (existing.exists()) {
      return false;
    }
    const value: Record<string, string | number> = {
      characterId: entry.characterId,
      revealAt: entry.revealAtMs,
      createdAt: entry.createdAtMs,
    };
    await update(ref(database), { [path]: value }).catch((error) => {
      throw this.toCoreError(error, `Failed to write /${path}`);
    });
    return true;
  }

  public async remove(
    userId: string,
    partnershipId: string,
    cardIds: string[],
  ): Promise<void> {
    if (cardIds.length === 0) {
      return;
    }
    const base = this.partnershipPath(userId, partnershipId);
    const updates: Record<string, null> = {};
    cardIds.forEach((cardId) => {
      updates[`${base}/${cardId}`] = null;
    });
    await update(ref(database), updates).catch((error) => {
      throw this.toCoreError(
        error,
        `Failed to remove held matches under /${base}`,
      );
    });
  }

  /** Deletes every hold of the partnership; returns what was removed. */
  public async purgeForPartnership(
    userId: string,
    partnershipId: string,
  ): Promise<HeldCharacterMatch[]> {
    const entries = await this.loadForPartnership(userId, partnershipId);
    const path = this.partnershipPath(userId, partnershipId);
    await update(ref(database), { [path]: null }).catch((error) => {
      throw this.toCoreError(error, `Failed to purge /${path}`);
    });
    return entries;
  }

  private toEntry(
    partnershipId: string,
    cardId: string,
    raw: unknown,
  ): HeldCharacterMatch | null {
    if (!raw || typeof raw !== "object") {
      return null;
    }
    const record = raw as Record<string, unknown>;
    const characterId = record.characterId;
    const revealAt = record.revealAt;
    const createdAt = record.createdAt;
    if (
      typeof characterId !== "string" ||
      typeof revealAt !== "number" ||
      typeof createdAt !== "number"
    ) {
      return null;
    }
    const revealedAt = record.revealedAt;
    return {
      partnershipId,
      characterId,
      cardId,
      revealAtMs: revealAt,
      createdAtMs: createdAt,
      ...(typeof revealedAt === "number" ? { revealedAtMs: revealedAt } : {}),
    };
  }
}
